import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Eye, Heart, MessageCircle, Zap, Infinity } from 'lucide-react';
import { useMyPlanLimits } from '@/hooks/useMyPlanLimits';

export const LimitsUsageCard: React.FC = () => {
  const { likes, profileViews, newConversations, boosts, isLoading } = useMyPlanLimits();

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-luxury-pinkBright';
  };

  const renderLimitItem = (
    icon: React.ReactNode,
    label: string,
    current: number,
    limit: number,
    isUnlimited: boolean
  ) => {
    const percentage = isUnlimited ? 0 : (current / limit) * 100;
    const progressColor = getProgressColor(percentage);

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm text-white/80">{label}</span>
          </div>
          <span className="text-sm font-medium text-white">
            {isUnlimited ? (
              <div className="flex items-center gap-1 text-luxury-pinkBright">
                <Infinity className="h-4 w-4" />
                <span>Ilimitado</span>
              </div>
            ) : (
              `${current}/${limit}`
            )}
          </span>
        </div>
        {!isUnlimited && (
          <Progress 
            value={percentage} 
            className="h-2 bg-white/10"
            indicatorClassName={progressColor}
          />
        )}
      </div>
    );
  };

  const renderBoostsItem = () => {
    const total = boosts.available + boosts.used;
    const usedPercentage = total > 0 ? (boosts.used / total) * 100 : 0;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-luxury-pinkBright" />
            <span className="text-sm text-white/80">Boosts</span>
          </div>
          <span className="text-sm font-medium text-white">
            {boosts.available} disponíveis
          </span>
        </div>
        {total > 0 && (
          <Progress 
            value={100 - usedPercentage} 
            className="h-2 bg-white/10"
            indicatorClassName="bg-luxury-pinkBright"
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white text-xl font-semibold">
            Uso dos Recursos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-white/10 rounded w-3/4"></div>
                <div className="h-2 bg-white/10 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-white text-xl font-semibold flex items-center gap-2">
          <span>Uso dos Recursos</span>
          <span className="text-xs text-white/60 font-normal">(Diário)</span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {renderLimitItem(
          <Eye className="h-4 w-4 text-luxury-pinkBright" />,
          'Visualizações de Perfis',
          profileViews.current,
          profileViews.limit,
          profileViews.isUnlimited
        )}
        
        {renderLimitItem(
          <Heart className="h-4 w-4 text-luxury-pinkBright" />,
          'Curtidas',
          likes.current,
          likes.limit,
          likes.isUnlimited
        )}
        
        {renderLimitItem(
          <MessageCircle className="h-4 w-4 text-luxury-pinkBright" />,
          'Novas Conversas',
          newConversations.current,
          newConversations.limit,
          newConversations.isUnlimited
        )}
        
        {renderBoostsItem()}
      </CardContent>
    </Card>
  );
};
